import React, { FC, useEffect, useState } from 'react';
import { Button } from '@thdinc/hpf-shared';
import FormModal from '@/components/modals/FormModal';

interface ExistingTemplate {
    id: string;
    title: string;
}

interface SaveTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    templateName: string;
    onTemplateNameChange: (name: string) => void;
    onSave: () => void;
    showUpdateOption?: boolean;
    onUpdate?: () => void;
    existingTemplates?: ExistingTemplate[];
    currentTemplateId?: string;
    isSaving?: boolean;
    isUpdating?: boolean;
    isExistingTemplatesLoading?: boolean;
}

const SaveTemplateModal: FC<SaveTemplateModalProps> = ({
    isOpen,
    onClose,
    templateName,
    onTemplateNameChange,
    onSave,
    showUpdateOption = false,
    onUpdate,
    existingTemplates = [],
    currentTemplateId,
    isSaving = false,
    isUpdating = false,
    isExistingTemplatesLoading = false,
}) => {
    const [error, setError] = useState('');
    const isSubmitting = isSaving || isUpdating;
    const isBusy = isSubmitting || isExistingTemplatesLoading;

    useEffect(() => {
        if (isOpen) setError('');
    }, [isOpen]);

    const isDuplicateName = (excludeId?: string) => {
        const normalized = templateName.trim().toLowerCase();
        if (!normalized) return false;
        return existingTemplates.some(
            (t) => t.id !== excludeId && (t.title || '').trim().toLowerCase() === normalized,
        );
    };

    const handleSave = () => {
        if (isDuplicateName()) {
            setError('A template with this name already exists. Please choose a different name.');
            return;
        }
        onSave();
    };

    const handleUpdate = () => {
        if (isDuplicateName(currentTemplateId)) {
            setError('A template with this name already exists. Please choose a different name.');
            return;
        }
        onUpdate && onUpdate();
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    return (
        <FormModal
            overflow='overflow-hidden'
            modalTitle='Save template'
            isOpen={isOpen}
            onClose={handleClose}
            disableOutsideClick={isSubmitting}
            width='lg:w-1/3 md:w-5/6 w-full'
        >
            <div>
                <h1 className='font-semibold text-sm mt-2 text-gray-700'>
                    Template Name
                </h1>
                <div className='relative'>
                    <input
                        value={templateName}
                        maxLength={100}
                        disabled={isSubmitting}
                        className='w-full px-4 py-3 mt-3 bg-p-gray rounded-full focus:outline-none focus:ring-1 focus:ring-primary-red text-gray-600 placeholder:text-[12px]'
                        placeholder={'Template Name'}
                        onChange={(e) => {
                            onTemplateNameChange(e.target.value);
                            if (error) setError('');
                        }}
                    />
                </div>
                {error && (
                    <p className='text-xs text-primary-red mt-2 px-2'>{error}</p>
                )}
                {!error && isExistingTemplatesLoading && (
                    <p className='text-xs text-gray-500 mt-2 px-2'>Checking existing template names...</p>
                )}
                <div className='flex justify-end gap-3 mt-6'>
                    {showUpdateOption && (
                        <Button
                            onClick={handleUpdate}
                            disabled={templateName?.trim() === '' || isBusy}
                            type='button'
                            className='text-center px-6 rounded-full py-2 text-white bg-black hover:bg-gray-800'
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </Button>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={templateName?.trim() === '' || isBusy}
                        type='button'
                        className={`text-center px-6 rounded-full py-2
                          ${'text-white'} ${'bg-primary-red hover:bg-primary-red-button-hover'}`}
                    >
                        {isSaving ? 'Saving...' : showUpdateOption ? 'Save as New' : 'Save Template'}
                    </Button>
                </div>
            </div>
        </FormModal>
    );
};

export default SaveTemplateModal;
